// Phase 4 vocabulary shared by the completion components: labels + helper
// copy per requirement kind / verification kind, the structural checks a
// policy definition must pass before it is sent (they mirror the server's
// 400s — unknown kind, duplicate kind, score without threshold, threshold
// outside 0–100, empty list, event_type off a system_action — and never
// evaluate whether a learner has met anything), the Phase 6 event-label lookup,
// and the deep link to the admin "Practice / Assessments" tab.
import type { CompletionPolicyInput, PolicyMode, RequirementKind, SubmissionStatus, SystemEvent, VerificationKind } from '@/services/learningService';
import { REQUIREMENT_KINDS } from '@/services/learningService';
import type { TFn } from './i18n';

export const requirementLabel = (t: TFn, kind: RequirementKind | string): string => ({
  attendance: t('training:req_attendance', 'Attendance'),
  score: t('training:req_score', 'Minimum score'),
  practice: t('training:req_practice', 'Practical exercise validated'),
  evidence: t('training:req_evidence', 'Evidence submitted'),
  manager_signoff: t('training:req_manager_signoff', 'Manager sign-off'),
  trainer_signoff: t('training:req_trainer_signoff', 'Trainer sign-off'),
  system_action: t('training:req_system_action', 'System-verified action'),
} as Record<string, string>)[kind] || String(kind);

export const requirementHelp = (t: TFn, kind: RequirementKind | string): string => ({
  attendance: t('training:req_attendance_help', 'Attended the session. Optional threshold: minimum % of the session duration.'),
  score: t('training:req_score_help', 'A recorded score at or above the threshold (0–100). Threshold required.'),
  practice: t('training:req_practice_help', 'The bound practical exercise has been submitted and validated.'),
  evidence: t('training:req_evidence_help', 'An evidence link was submitted on the assignment.'),
  manager_signoff: t('training:req_manager_signoff_help', 'A people manager in reach (or an administrator) signed off.'),
  trainer_signoff: t('training:req_trainer_signoff_help', 'The session trainer (or an administrator) signed off.'),
  system_action: t('training:req_system_action_help', 'Satisfied when the learner performs this action in KaziPeople after the session starts.'),
} as Record<string, string>)[kind] || '';

/** Kinds that accept a threshold; only `score` REQUIRES one. */
export const kindTakesThreshold = (kind: RequirementKind | string): boolean => kind === 'attendance' || kind === 'score';
export const kindRequiresThreshold = (kind: RequirementKind | string): boolean => kind === 'score';
export const isSystemKind = (kind: RequirementKind | string): boolean => kind === 'system_action';

export const modeLabel = (t: TFn, mode: PolicyMode | string | undefined): string =>
  mode === 'any_of' ? t('training:policy_mode_any', 'Any one of') : t('training:policy_mode_all', 'All required');

export const verificationLabel = (t: TFn, kind: VerificationKind | string): string => ({
  manual: t('training:verify_manual', 'Self-declared'),
  trainer: t('training:verify_trainer', 'Trainer validates'),
  manager: t('training:verify_manager', 'Manager validates'),
  system: t('training:verify_system', 'System verifies'),
} as Record<string, string>)[kind] || String(kind);

export const verificationHelp = (t: TFn, kind: VerificationKind | string): string => ({
  manual: t('training:verify_manual_help', 'The submission is validated the moment the learner submits it.'),
  trainer: t('training:verify_trainer_help', 'The session trainer (or an administrator) reviews and validates or rejects.'),
  manager: t('training:verify_manager_help', 'A people manager in reach (or an administrator) reviews and validates or rejects.'),
  system: t('training:verify_system_help', 'An allow-listed platform event matching the rule validates it automatically — the learner does not submit anything.'),
} as Record<string, string>)[kind] || '';

export const submissionStatusLabel = (t: TFn, status: SubmissionStatus | string | undefined): string => ({
  pending: t('training:sub_pending', 'Not submitted'),
  submitted: t('training:sub_submitted', 'Awaiting validation'),
  validated: t('training:sub_validated', 'Validated'),
  rejected: t('training:sub_rejected', 'Rejected'),
} as Record<string, string>)[status || ''] || String(status || '');

export const SUBMISSION_STATUS_CLS: Record<string, string> = {
  pending: 'bg-muted text-muted-foreground',
  submitted: 'bg-blue-100 text-blue-700 dark:bg-blue-500/[0.16] dark:text-blue-300',
  validated: 'bg-success-soft text-success-strong',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-500/[0.16] dark:text-red-300',
};

/** "work.completed" → "work completed": the fallback when the catalog is not readable (learners) or does not list the type. */
export const humanizeEventType = (eventType: string): string => eventType.replace(/[._]+/g, ' ').trim();
/** The catalog label for an event type, else the humanized type. Never invents a description. */
export const systemEventLabel = (events: SystemEvent[] | undefined, eventType: string | undefined | null): string => {
  if (!eventType) return '';
  return events?.find((e) => e.event_type === eventType)?.label || humanizeEventType(eventType);
};

/** Structural checks on a policy definition (what the server would 400 on). Returns translated messages; empty = OK to send. */
export function validatePolicyInput(t: TFn, input: CompletionPolicyInput): string[] {
  const errs: string[] = [];
  if (!input.name.trim()) errs.push(t('training:policy_err_name', 'Give the policy a name.'));
  if (input.requirements.length === 0) errs.push(t('training:policy_err_empty', 'Add at least one requirement.'));
  const seen = new Set<string>();
  input.requirements.forEach((r) => {
    if (!(REQUIREMENT_KINDS as string[]).includes(r.kind)) errs.push(t('training:policy_err_kind', 'Unknown requirement kind "{{kind}}".', { kind: r.kind }));
    if (seen.has(r.kind)) errs.push(t('training:policy_err_dup', 'Each requirement kind can appear only once ({{kind}}).', { kind: requirementLabel(t, r.kind) }));
    seen.add(r.kind);
    const th = r.threshold;
    if (kindRequiresThreshold(r.kind) && (th === undefined || th === null || !Number.isFinite(th))) errs.push(t('training:policy_err_score_threshold', 'Minimum score needs a threshold (0–100).'));
    if (th !== undefined && th !== null && (!Number.isFinite(th) || th < 0 || th > 100)) errs.push(t('training:policy_err_threshold_range', '{{kind}}: threshold must be between 0 and 100.', { kind: requirementLabel(t, r.kind) }));
    if (r.event_type && !isSystemKind(r.kind)) errs.push(t('training:policy_err_event_kind', 'A platform event only applies to a system-verified action ({{kind}}).', { kind: requirementLabel(t, r.kind) }));
  });
  return errs;
}

/** Path of the admin "Practice / Assessments" tab from any tenant page (training, team-learning, admin/learning, …). */
export function manageCompletionHref(pathname: string): string {
  const base = pathname.replace(/\/(training(\/.*)?|team-learning|admin\/learning)\/?$/, '');
  return `${base}/admin/learning?tab=practice`;
}
